import { firestoreFieldValueDelete, allFromSnapshot, allFromSnapshotByUid, firstFromSnapshot, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { guid, silentLogger } from "@buildwithflux/shared";
import { BaseUserRepository } from "./BaseUserRepository";
/**
 * Interface to user data as stored in Firestore.
 */
export class FirestoreUserRepository extends BaseUserRepository {
    db;
    logger;
    firestoreUserByUidChangeSubscriptions = new Map();
    firestoreUserByHandleChangeSubscriptions = new Map();
    constructor(db, logger = silentLogger) {
        super();
        this.db = db;
        this.logger = logger;
    }
    /** @inheritDoc */
    delete(user) {
        return this.db.user(user.handle).delete().then();
    }
    /** @inheritDoc */
    async getByHandle(handle) {
        return oneFromSnapshot(await this.db.user(handle).get(), () => undefined);
    }
    /**
     * @inheritDoc
     * @see https://stackoverflow.com/questions/46568142
     */
    async getByHandlePrefix(handlePrefix, { limit = 50 }) {
        return allFromSnapshot(await this.db
            .userCollection()
            .where("handle", ">=", handlePrefix)
            .where("handle", "<=", handlePrefix + "\uf8ff")
            .where("isAnonymous", "==", false)
            // NOTE: need a new index to do this... not sure if it is worth it
            // .orderBy("last_active_at", "desc")
            .limit(limit)
            .get());
    }
    /** @inheritDoc */
    async getByUid(userUid) {
        return firstFromSnapshot(await this.db.userCollection().where("uid", "==", userUid).limit(1).get(), () => undefined);
    }
    /** @inheritDoc */
    async getByEmail(email) {
        return firstFromSnapshot(await this.db.userCollection().where("email", "==", email).limit(1).get(), () => undefined);
    }
    /** @inheritDoc */
    async getByUids(userUids) {
        // Prevents "Invalid Query. A non-empty array is required for 'in' filters."
        if (userUids.length === 0) {
            return Promise.resolve({});
        }
        return allFromSnapshotByUid(await this.db.userCollection().where("uid", "in", userUids).get());
    }
    /** @inheritDoc */
    async getAllByUid(userUid) {
        return allFromSnapshot(await this.db.userCollection().where("uid", "==", userUid).get());
    }
    /** @inheritDoc */
    async save(userData) {
        return this.db.userCollection().doc(userData.handle).set(userData).then();
    }
    /** @inheritDoc */
    updateDataIsValid(potentialUpdate) {
        return !("uid" in potentialUpdate || "handle" in potentialUpdate);
    }
    /** @inheritDoc */
    async update(userHandle, updateWith) {
        return this.updateExisting(userHandle, updateWith);
    }
    async updateDocumentDataSentinel(userUid) {
        const snapshot = await this.db.userCollection().where("uid", "==", userUid).get();
        if (snapshot.docs.length < 1) {
            this.logger.info("Nothing to do: user does not exist");
            return;
        }
        if (snapshot.docs.length > 1) {
            this.logger.warn(`Multiple users with same UID: ${userUid}`);
        }
        // Bump the updated_at and the document_data_sentinel
        await Promise.all(snapshot.docs.map((user) => user.ref.set({ updated_at: Date.now(), document_data_sentinel: guid() }, { merge: true })));
    }
    /** @inheritDoc */
    subscribeToUser(userUid, onChange) {
        const userRef = this.db.userCollection().where("uid", "==", userUid);
        const unsubscribe = this.subscriptionMgrByUid.addSubscription(userUid, onChange, () => {
            const existingSubCount = this.subscriptionMgrByUid.listenerCount(userUid);
            if (existingSubCount === 0) {
                const firestoreUnsub = this.firestoreUserByUidChangeSubscriptions.get(userUid);
                if (firestoreUnsub) {
                    firestoreUnsub();
                    this.firestoreUserByUidChangeSubscriptions.delete(userUid);
                }
            }
        });
        // NOTE: We're using a multiplexing pattern here.  There may be multiple logical subscribers to firestore,
        // but we create exactly one subscription here.  When the subscription is triggered, it causes all of the
        // logical subscriptions to fire as well.
        const existing = this.firestoreUserByUidChangeSubscriptions.get(userUid);
        if (!existing) {
            const firestoreUnsubscriber = userRef.onSnapshot((snap) => {
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                snap.docChanges().forEach((change) => {
                    switch (change.type) {
                        case "added": {
                            this.subscriptionMgrByUid.notify(userUid, { type: "created", data: change.doc.data() });
                            break;
                        }
                        case "removed": {
                            this.subscriptionMgrByUid.notify(userUid, { type: "deleted" });
                            break;
                        }
                        case "modified": {
                            this.subscriptionMgrByUid.notify(userUid, { type: "updated", data: change.doc.data() });
                            break;
                        }
                    }
                });
            }, (error) => {
                this.subscriptionMgrByUid.notify(userUid, { type: "error", error });
            });
            this.firestoreUserByUidChangeSubscriptions.set(userUid, firestoreUnsubscriber);
        }
        // NOTE: custom cleanup logic here: we check to see if there are any subscribers left.  As long as there are,
        // we keep our subscription open.  Once there aren't any remaining, we shut it down.
        return unsubscribe;
    }
    /** @inheritDoc */
    subscribeToUserHandle(handle, onChange) {
        const userRef = this.db.user(handle);
        const unsubscribe = this.subscriptionMgrByHandle.addSubscription(handle, onChange, () => {
            const existingSubCount = this.subscriptionMgrByHandle.listenerCount(handle);
            if (existingSubCount === 0) {
                const firestoreUnsub = this.firestoreUserByHandleChangeSubscriptions.get(handle);
                if (firestoreUnsub) {
                    firestoreUnsub();
                    this.firestoreUserByHandleChangeSubscriptions.delete(handle);
                }
            }
        });
        // NOTE: We're using a multiplexing pattern here.  There may be multiple logical subscribers to firestore,
        // but we create exactly one subscription here.  When the subscription is triggered, it causes all of the
        // logical subscriptions to fire as well.
        const existing = this.firestoreUserByHandleChangeSubscriptions.get(handle);
        if (!existing) {
            const firestoreUnsubscriber = userRef.onSnapshot((snap) => {
                const user = oneFromSnapshot(snap, () => undefined);
                if (user) {
                    this.subscriptionMgrByHandle.notify(handle, { type: "updated", data: user });
                }
                else {
                    this.subscriptionMgrByHandle.notify(handle, { type: "deleted" });
                }
            }, (error) => {
                this.subscriptionMgrByHandle.notify(handle, { type: "error", error });
            });
            this.firestoreUserByHandleChangeSubscriptions.set(handle, firestoreUnsubscriber);
        }
        // NOTE: custom cleanup logic here: we check to see if there are any subscribers left.  As long as there are,
        // we keep our subscription open.  Once there aren't any remaining, we shut it down.
        return unsubscribe;
    }
    /** @inheritDoc */
    async removeProjectTemplate(handle, documentUid) {
        await this.db.user(handle).update({
            [`project_templates.${documentUid}`]: await firestoreFieldValueDelete(this.db),
        });
    }
    /** @inheritDoc */
    async updateOrganizationMembershipSentinel(userUid) {
        const snapshot = await this.db.userCollection().where("uid", "==", userUid).get();
        if (snapshot.docs.length < 1) {
            this.logger.info("Nothing to do: user does not exist");
            return;
        }
        if (snapshot.docs.length > 1) {
            this.logger.warn(`Multiple users with same UID: ${userUid}`);
        }
        // Bump the updated_at and the organization_membership_sentinel
        await Promise.all(snapshot.docs.map((user) => user.ref.set({ updated_at: Date.now(), organization_membership_sentinel: guid() }, { merge: true })));
    }
    /**
     * @deprecated Not transactional, so not safe with concurrent writes
     */
    async updateExisting(userHandle, update) {
        const ref = this.db.user(userHandle);
        if ((await ref.get()).exists) {
            return ref.set(update, { merge: true }).then();
        }
        else
            throw new Error(`Failed to update non-existent user document for handle ${userHandle}`);
    }
}
//# sourceMappingURL=FirestoreUserRepository.js.map